// 5. MODAL POUR ANALYSE - SELECTION MULTIPLE
// components/consultation/AnalyseModal.tsx
import React, { useState, useRef, useEffect } from "react";
import { ClipboardList, X, Check, Search, Loader2, Square, CheckSquare, PlusCircle } from "lucide-react";
import type { Analyse } from "../../../types/analyse";
import Modal from "../../ui/Modal";
import { CATEGORY_LABELS, type AnalysisTypeByCategory } from "../../../services/analysisTypeService";
import { useAnalysisTypes } from "../../../hooks/useAnalysisTypes";

interface AnalyseModalProps {
  isOpen: boolean;
  onClose: () => void;
  analyse: Analyse;
  onChange: (analyse: Analyse) => void;
  onSubmit: () => void;
  onSubmitMultiple?: (analyses: Analyse[]) => void;
  isEditing: boolean;
}

export const AnalyseModal: React.FC<AnalyseModalProps> = ({
  isOpen,
  onClose,
  analyse,
  onChange,
  onSubmit,
  onSubmitMultiple,
  isEditing,
}) => {
  // Utiliser le hook avec cache global (les données sont chargées une seule fois)
  const { analysisData, loading, error, reload } = useAnalysisTypes();
  const [selectedAnalyses, setSelectedAnalyses] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [customAnalyses, setCustomAnalyses] = useState<string[]>([]);
  const [newAnalysis, setNewAnalysis] = useState("");
  const [instructions, setInstructions] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset selection when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedAnalyses(new Set());
      setSearchTerm("");
      setCustomAnalyses([]);
      setNewAnalysis("");
      setInstructions("");
    }
  }, [isOpen]);

  const addCustomAnalysis = () => {
    if (newAnalysis.trim()) {
      setCustomAnalyses([...customAnalyses, newAnalysis.trim()]);
      setNewAnalysis("");
    }
  };

  const removeCustomAnalysis = (index: number) => {
    setCustomAnalyses(customAnalyses.filter((_, i) => i !== index));
  };

  const toggleAnalysis = (analysisName: string) => {
    const newSelected = new Set(selectedAnalyses);
    if (newSelected.has(analysisName)) {
      newSelected.delete(analysisName);
    } else {
      newSelected.add(analysisName);
    }
    setSelectedAnalyses(newSelected);
  };

  const selectAllInCategory = (category: AnalysisTypeByCategory) => {
    const newSelected = new Set(selectedAnalyses);
    const allSelected = category.analyses.every((a) => selectedAnalyses.has(a.name));

    if (allSelected) {
      // Désélectionner tous
      category.analyses.forEach((a) => newSelected.delete(a.name));
    } else {
      // Sélectionner tous
      category.analyses.forEach((a) => newSelected.add(a.name));
    }
    setSelectedAnalyses(newSelected);
  };

  // Normalise les chaînes pour recherche insensible aux accents et à la casse
  const normalizeString = (s: string = "") =>
    s
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  // Filtre les analyses selon le terme de recherche
  const getFilteredData = () => {
    const normalizedSearch = normalizeString(searchTerm);
    if (!normalizedSearch) return analysisData;

    return analysisData
      .map((category) => ({
        ...category,
        analyses: category.analyses.filter(
          (a) =>
            normalizeString(a.name).includes(normalizedSearch) ||
            (a.description && normalizeString(a.description).includes(normalizedSearch))
        ),
      }))
      .filter((category) => category.analyses.length > 0);
  };

  const filteredData = getFilteredData();

  const handleSubmit = () => {
    if (isEditing) {
      // Mode édition : une seule analyse
      onSubmit();
    } else if (selectedAnalyses.size > 0 && onSubmitMultiple) {
      // Mode création multiple
      const analysesToAdd: Analyse[] = Array.from(selectedAnalyses).map((name) => ({
        nom: name,
        label: name,
        reason: "",
        diagnostics: "",
        conseils: instructions || "", // Ajouter les instructions
      }));
      
      // Ajouter les analyses personnalisées
      const customAnalysesToAdd: Analyse[] = customAnalyses.map((analysis: string) => ({
        nom: analysis,
        label: analysis,
        reason: "Analyse personnalisée",
        diagnostics: "",
        conseils: instructions || "",
      }));
      
      onSubmitMultiple([...analysesToAdd, ...customAnalysesToAdd]);
      onClose();
    } else if (selectedAnalyses.size > 0) {
      // Fallback : ajouter une par une
      const firstAnalysis = Array.from(selectedAnalyses)[0];
      onChange({ ...analyse, nom: firstAnalysis, label: firstAnalysis, conseils: instructions || "" });
      onSubmit();
    } else if (customAnalyses.length > 0 && onSubmitMultiple) {
      // Uniquement des analyses personnalisées
      const customAnalysesToAdd: Analyse[] = customAnalyses.map((analysis: string) => ({
        nom: analysis,
        label: analysis,
        reason: "Analyse personnalisée",
        diagnostics: "",
        conseils: instructions || "",
      }));
      
      onSubmitMultiple(customAnalysesToAdd);
      onClose();
    }
  };

  // Mode édition : afficher le formulaire classique
  if (isEditing) {
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center">
              <div className="bg-blue-100 p-2 rounded-xl mr-3">
                <ClipboardList className="text-blue-600" size={24} />
              </div>
              Modifier l'analyse
            </h4>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Nom de l'examen *
              </label>
              <input
                type="text"
                value={analyse.label || ""}
                onChange={(e) => onChange({ ...analyse, label: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                placeholder="Nom de l'examen"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Raison de l'examen
              </label>
              <input
                type="text"
                value={analyse.reason}
                onChange={(e) => onChange({ ...analyse, reason: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                placeholder="Justification de l'examen"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Diagnostics concernés
              </label>
              <input
                type="text"
                value={analyse.diagnostics}
                onChange={(e) => onChange({ ...analyse, diagnostics: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                placeholder="Diagnostic(s) motivant l'examen"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Conseils au patient
              </label>
              <textarea
                value={analyse.conseils}
                onChange={(e) => onChange({ ...analyse, conseils: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none"
                rows={3}
                placeholder="Conseils à donner au patient"
              />
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-gray-100">
            <button
              type="button"
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 font-medium"
              onClick={onClose}
            >
              Annuler
            </button>
            <button
              type="button"
              className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg font-medium"
              onClick={onSubmit}
              disabled={!analyse.label}
            >
              <Check className="mr-2" size={18} />
              Modifier
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Définir les colonnes selon la photo
  const COLUMNS = [
    ["HEMATOLOGIE", "SEROLOGIE", "BACTERIOLOGIE_PARASITOLOGIE"],
    ["BIOCHIMIE_SANGUINE", "BIOCHIMIE_URINAIRE"],
    ["ENDOCRINOLOGIE", "MARQUEURS_TUMORAUX", "DEPISTAGES"],
  ];

  // Mode création : sélection multiple
  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="max-w-7xl">
      <div className="p-6 md:p-8 max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between mb-6">
          <h4 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center">
            <div className="bg-blue-100 p-2 rounded-xl mr-3">
              <ClipboardList className="text-blue-600" size={24} />
            </div>
            Prescription des examens
          </h4>
          <div className="flex items-center gap-4">
            <div className="relative w-64 md:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border-2 border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm"
                placeholder="Rechercher un examen..."
              />
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Compteur de sélection et barre de recherche rapide */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
          {selectedAnalyses.size > 0 ? (
            <div className="px-4 py-2 bg-blue-50 border border-blue-100 rounded-xl flex items-center gap-4">
              <span className="text-blue-700 font-semibold text-sm">
                {selectedAnalyses.size} examen{selectedAnalyses.size > 1 ? "s" : ""} sélectionné{selectedAnalyses.size > 1 ? "s" : ""}
              </span>
              <button
                onClick={() => setSelectedAnalyses(new Set())}
                className="text-blue-600 hover:text-blue-800 text-xs font-bold uppercase tracking-wider"
              >
                Tout effacer
              </button>
            </div>
          ) : (
            <div className="text-gray-400 text-sm italic">
              Cochez les cases pour ajouter des examens à la prescription
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-medium text-sm"
              onClick={onClose}
            >
              Annuler
            </button>
            <button
              type="button"
              className="flex items-center px-8 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all shadow-md font-bold text-sm disabled:opacity-50"
              onClick={handleSubmit}
              disabled={selectedAnalyses.size === 0}
            >
              <Check className="mr-2" size={18} />
              Valider la sélection ({selectedAnalyses.size})
            </button>
          </div>
        </div>

        {/* Liste des analyses en 3 colonnes */}
        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
          {loading ? (
            <div className="p-20 text-center text-gray-500">
              <Loader2 className="animate-spin mx-auto mb-4" size={40} />
              <p className="text-lg">Chargement du formulaire...</p>
            </div>
          ) : error ? (
            <div className="p-10 text-center text-red-500">
              <p>{error}</p>
              <button onClick={reload} className="mt-4 text-blue-600 hover:underline font-bold">
                Réessayer
              </button>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="p-20 text-center text-gray-500">
              <ClipboardList className="mx-auto mb-4 opacity-20" size={64} />
              <p className="font-medium text-lg">Aucun examen trouvé pour "{searchTerm}"</p>
              <button
                onClick={() => {
                  if (searchTerm.trim()) {
                    toggleAnalysis(searchTerm.trim());
                    setSearchTerm("");
                  }
                }}
                className="mt-6 px-6 py-3 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 transition-all font-bold flex items-center mx-auto"
              >
                <PlusCircle className="mr-2" size={20} />
                Ajouter "{searchTerm}" comme examen personnalisé
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {COLUMNS.map((colCategories, colIndex) => (
                <div key={`col-${colIndex}`} className="space-y-6">
                  {colCategories.map((catKey) => {
                    const categoryData = filteredData.find((d) => d.category === catKey);
                    if (!categoryData) return null;

                    const allSelected = categoryData.analyses.every((a) => selectedAnalyses.has(a.name));

                    return (
                      <div key={catKey} className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
                        <div
                          className="px-4 py-2 border-b border-gray-100 flex items-center justify-between bg-blue-50/30"
                        >
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => selectAllInCategory(categoryData)}
                              className="text-gray-400 hover:text-blue-600 transition-colors"
                            >
                              {allSelected ? (
                                <CheckSquare size={18} className="text-blue-600" />
                              ) : (
                                <Square size={18} />
                              )}
                            </button>
                            <span className="font-bold text-gray-800 text-xs sm:text-sm uppercase tracking-wider">
                              {CATEGORY_LABELS[catKey] || catKey}
                            </span>
                          </div>
                        </div>
                        <div className="p-2 space-y-0.5">
                          {categoryData.analyses.map((exam) => {
                            const isSelected = selectedAnalyses.has(exam.name);
                            return (
                              <div
                                key={exam.id}
                                className={`px-2 py-1.5 flex items-center gap-2 cursor-pointer rounded-lg transition-all ${isSelected ? "bg-blue-50" : "hover:bg-gray-50"
                                  }`}
                                onClick={() => toggleAnalysis(exam.name)}
                              >
                                {isSelected ? (
                                  <CheckSquare size={16} className="text-blue-600 flex-shrink-0" />
                                ) : (
                                  <Square size={16} className="text-gray-300 flex-shrink-0" />
                                )}
                                <span className={`text-[13px] line-clamp-1 ${isSelected ? "text-blue-700 font-semibold" : "text-gray-700"}`}>
                                  {exam.name}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {/* Section AUTRES dans la 3ème colonne */}
                  {colIndex === 2 && !searchTerm && (
                    <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
                      <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
                        <span className="font-bold text-gray-800 text-xs uppercase tracking-wider">AUTRES</span>
                      </div>
                      <div className="p-4 space-y-4">
                        {/* Section Analyses Personnalisées */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            � Analyses personnalisées
                          </label>
                          <div className="space-y-2 mb-3">
                            {customAnalyses.map((analysis, index: number) => (
                              <div key={index} className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                                <span className="text-sm text-gray-700 flex-1">{analysis}</span>
                                <button
                                  onClick={() => removeCustomAnalysis(index)}
                                  className="text-red-500 hover:text-red-700 transition-colors"
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newAnalysis}
                              onChange={(e) => setNewAnalysis(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && addCustomAnalysis()}
                              placeholder="Ajouter une analyse..."
                              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                            <button
                              onClick={addCustomAnalysis}
                              disabled={!newAnalysis.trim()}
                              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <PlusCircle size={16} />
                            </button>
                          </div>
                        </div>

                        {/* Section Instructions */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Instructions spéciales
                          </label>
                          <textarea
                            value={instructions}
                            onChange={(e) => setInstructions(e.target.value)}
                            placeholder="Instructions spéciales pour le laboratoire..."
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                            rows={3}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};